//-- Copyright (c) 2021 TMT Insights Group, LLC
import { Box, Divider, Fade, Typography } from '@mui/material';
import { colorConsoleLog } from '@/_utils/utils';
import { selectJobAssets } from '@/redux/selectors/jobSelectors';
import NoDataMessage from '../../../_common/NoDataMessage';
import AssetListGrouped from './AssetListGrouped';
import AssetComponentBox from './AssetComponentBox';
import { AssetComponent, Assets } from '@/redux/slices/slice_types';
import { useAppSelector } from '@/redux/hooks';
import styled from '@emotion/styled';
import { useMemo } from 'react';

const isAlternate = (asset: AssetComponent, filterOptionals: boolean = false) => {
  if (filterOptionals) return !!asset.alternate_to || asset.optional;
  return !!asset.alternate_to;
};

const AssetList = () => {
  const assets: Assets = useAppSelector(selectJobAssets);

  if (!assets?.components?.length) return <NoDataMessage message="No assets" />;

  const alternates = useMemo(
    () => assets.components.filter((asset) => isAlternate(asset)).sort((a, b) => (b.alternate_hierarchy || 0) - (a.alternate_hierarchy || 0)),
    [assets.components]
  );
  const optionalAssets = useMemo(
    () =>
      assets.components
        .filter((asset) => asset.optional)
        .sort((a, b) => {
          // Define type priority: video (1), audio (2), subtitle (3), everything else (4)
          const getTypePriority = (type: string) => {
            switch (type) {
              case 'video':
                return 1;
              case 'audio':
                return 2;
              case 'subtitle':
              case 'captions':
                return 3;
              default:
                return 4;
            }
          };

          const aPriority = getTypePriority(a.type);
          const bPriority = getTypePriority(b.type);

          // First sort by type priority
          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }

          // Then sort by asset label within the same type
          return (a.label || '').localeCompare(b.label || '');
        }),
    [assets.components]
  );

  const videoAssets = assets.components?.filter((asset) => asset.type === 'video' && !isAlternate(asset, true)) || [];
  const audioAssets = assets.components?.filter((asset) => asset.type === 'audio' && !isAlternate(asset, true)) || [];
  const subtitleAssets =
    assets.components?.filter((asset) => (asset.type === 'subtitle' || asset.type === 'captions') && !isAlternate(asset, true)) || [];
  const other =
    assets.components?.filter(
      (asset) =>
        asset.type !== 'video' && asset.type !== 'audio' && asset.type !== 'subtitle' && asset.type !== 'captions' && !isAlternate(asset, true)
    ) || [];

  alternates.forEach((alt) => {
    if (alt.type === 'video') {
      const index = videoAssets.findIndex((a) => a.local_id === alt.alternate_to);
      if (index !== -1) videoAssets.splice(index + 1, 0, alt);
    } else if (alt.type === 'audio') {
      const index = audioAssets.findIndex((a) => a.local_id === alt.alternate_to);
      if (index !== -1) audioAssets.splice(index + 1, 0, alt);
    } else if (alt.type === 'subtitle' || alt.type === 'captions') {
      const index = subtitleAssets.findIndex((a) => a.local_id === alt.alternate_to);
      if (index !== -1) subtitleAssets.splice(index + 1, 0, alt);
    } else {
      const index = other.findIndex((a) => a.local_id === alt.alternate_to);
      if (index !== -1) {
        other.splice(index + 1, 0, alt);
      } else {
        const altIndex = optionalAssets.findIndex((a) => a.local_id === alt.alternate_to);
        if (altIndex !== -1) {
          optionalAssets.splice(altIndex + 1, 0, alt);
        } else {
          other.push(alt);
        }
      }
    }
  });
  const sortedAssets = [...videoAssets, ...audioAssets, ...subtitleAssets, ...other];

  // if we are getting a version_name on the components, show the grouped view
  // this is to make it backwards compatible, as the version_name was added later
  if (sortedAssets[0].version_name && !sortedAssets[0].version_external_id) {
    colorConsoleLog('No version_external_id found in assets, cannot display grouping.', 'tomato');
  } else if (sortedAssets[0].version_name) return <AssetListGrouped assets={sortedAssets} />;

  return (
    <Box sx={{ pt: 1 }}>
      {sortedAssets.map((asset, i) => (
        <Fade key={`asset-${i}`} in timeout={(i + 1) * 300}>
          <StyledContainer isAlternate={+isAlternate(asset)}>
            <AssetComponentBox asset={asset} />
          </StyledContainer>
        </Fade>
      ))}
      {optionalAssets.length > 0 && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              OPTIONAL
            </Typography>
          </Divider>
          {optionalAssets.map((asset, i) => (
            <Fade key={`asset-${i}`} in timeout={(i + 1) * 300}>
              <StyledContainer isAlternate={+isAlternate(asset)}>
                <AssetComponentBox asset={asset} />
              </StyledContainer>
            </Fade>
          ))}
        </>
      )}
    </Box>
  );
};

export default AssetList;

const StyledContainer = styled.div<{ isAlternate: number }>`
  ${({ isAlternate, theme }) =>
    isAlternate
      ? `
    margin-left: 30px;
    margin-top: -5px;
    margin-bottom: 2px;
    position: relative;
    &:before {
     content: '→';
     position: absolute;
     left: -20px;
     top: 20px;
    }
     &:after {
      content: '';
      position: absolute;
      left: -20px;
      top: 22px;
      height: 8px;
      width: 1px;
      border-left: 2px solid ${theme.palette.text.primary};
    }
   @media (min-width: ${theme.breakpoints.values.xl}px) {
    &:after {
      height: 10px;
    }
  }
  `
      : ''}
`;
